import moment, { Moment } from 'moment-timezone';
import { get, snakeCase } from 'lodash';
import { TTypedTFunction } from '@lib/useTypedTranslation';
import { DeviceEventCountName } from '../../../../services/core/eventsTypes';
import { Filter } from '../../../../components/timePeriodAndFilterPicker/useFilterPicker';
import { ITranslationKeys } from '@components/i18n/keys';
import { AllWorldRoutes } from '../../../../routes/parts/allWorldRoutes';
import { encodeNullableQueryParam } from '../../../../lib/nullableQueryParam';
import { DataUsageDeviceType } from '../../../../services/core/performanceDataUsageDevices';

export function getEventsChartData(name: DeviceEventCountName) {
  return get({
    'deviceRebooted': {
      translationKey: 'REBOOTS',
      colourCode: '#2c678c'
    },
    'deviceLowPower': {
      translationKey: 'LOW_POWER',
      colourCode: '#2c678c'
    },
    'timeError': {
      translationKey: 'TIME_ERRORS',
      colourCode: '#2c678c'
    },
    'batteryChanged': {
      translationKey: 'BATTERY_SWAPS',
      colourCode: '#2c678c'
    },
    'deviceDropped': {
      translationKey: 'DROPS',
      colourCode: '#2c678c'
    },
    'simChanged': {
      translationKey: 'SIM_SWAPS',
      colourCode: '#2c678c'
    }
  }, name);
}

/** The devices a filter selects, as named in chart titles: e.g. a group's name, or 'All devices' when unfiltered */
export function getFilterLabel(chartFilter: Filter, t: TTypedTFunction): string {
  let filter = chartFilter.group?.value || chartFilter.homeLocation?.value.name || chartFilter.zone?.value.name || chartFilter.model?.value || t('ALL_DEVICES', { ns: 'filters' });
  if (chartFilter.group?.value === null) {
    filter = t('translation:NO_GROUP');
  } else if (chartFilter.homeLocation?.value.name === null) {
    filter = t('editRecord:NO_HOME_LOCATION');
  }
  return filter;
}

/** The time period of a chart, as named in chart titles: e.g. 'in the last 30 days' */
export function getPeriodLabel(days: number, t: TTypedTFunction): string {
  return `${t('IN_THE_LAST_M_other', { ns: 'timeState' })} ${t('DAY_COUNT', { ns: 'timeState', count: days })}`.toLowerCase();
}

export function getTitle(chartFilter: Filter, days: number, t: TTypedTFunction, name = 'DATA_USAGE', contextualTextApplied?: boolean) {
  const filter = getFilterLabel(chartFilter, t);
  const devicesFilter = ((chartFilter.group?.value || chartFilter.homeLocation?.value?.name || chartFilter.model?.value) || (chartFilter.group?.value === null || chartFilter.homeLocation?.value.name === null || chartFilter.zone?.value.name)) && contextualTextApplied;
  const period = getPeriodLabel(days, t);
  return devicesFilter ?
    t(`PERFORMANCE_CHART_TITLE_${name}` as keyof ITranslationKeys['performance'], { ns: 'performance', filter, period, context: 'withFilter' })
    :
    t(`PERFORMANCE_CHART_TITLE_${name}` as keyof ITranslationKeys['performance'], { ns: 'performance', filter, period });
}

export function getFormattedTitle(title: string) {
  return snakeCase(title);
}

// Builds the drill-down URL for a clicked, non-zero chart column, carrying the clicked
// event name/day plus the Performance tab's current pre-filter through to the new page.
export function getPerformanceDevicesPath(routes: AllWorldRoutes, name: string, utc: number, filter: Filter): string {
  const params = new URLSearchParams();
  params.set('name', name);
  params.set('utc', String(utc));
  encodeNullableQueryParam(params, 'homeLocationId', filter.homeLocation?.value.id);
  encodeNullableQueryParam(params, 'group', filter.group?.value);
  if (filter.zone?.value.id) {
    params.set('zoneId', filter.zone.value.id);
  }
  encodeNullableQueryParam(params, 'model', filter.model?.value);

  params.set('ts', String(Date.now()));
  return `${routes.core.performanceDevices}?${params.toString()}`;
}

// Not a real device event; identifies the Devices Utilization chart's click-through, which shares
// the same drill-down page/route as the event count charts but is backed by a different metric.
const UTILIZATION_NAME = 'utilisation';

export function getPerformanceUtilizationDevicesPath(routes: AllWorldRoutes, utc: number, filter: Filter): string {
  return getPerformanceDevicesPath(routes, UTILIZATION_NAME, utc, filter);
}

export function getPerformanceDataUsageDevicesPath(routes: AllWorldRoutes, type: DataUsageDeviceType, utc: number, filter: Filter): string {
  return getPerformanceDevicesPath(routes, type, utc, filter);
}

export const getUtcOffset = (date: Moment): number => {
  return date.isValid() ? date.utcOffset() : 0;
};

export const getTimeZoneOffset = (tz: string): number => {
  return moment.tz.names().includes(tz) ? getUtcOffset(moment.tz(tz)) : 0;
};
